import { h, Component, render } from 'preact';
import { joinClasses } from 'utils/utils';
import { getIcon } from 'utils/icons';
import { canUser } from 'utils/roles';

import Button from 'components/button/button';
import Spinner from 'components/spinner/spinner';

import s from 'utils/modal.sss';

export async function showModal(Component, options = {}, modalOptions = {}) {
	const container = document.createElement('div');
	render(<Component {...options} />, container);
	const dialog = container.firstElementChild;

	const { promise, resolve } = Promise.withResolvers();

	(modalOptions?.mountPoint || document.body).append(dialog);
	if (dialog.popover) {
		dialog.addEventListener('toggle', (e) => {
			if (e.newState === 'closed') {
				resolve(dialog.returnValue);
			}
		});
		dialog.showPopover();
	} else {
		// dialog.addEventListener('cancel', () => promise.resolve(null));
		dialog.addEventListener('close', () => resolve(dialog.returnValue));
		dialog.showModal();
		// Focus on dialog itself so elements inside are not focused by default (e.g. first input field)
		dialog.focus();
	}

	let result = await promise;

	// Close any nested popover
	dialog.querySelectorAll(':popover-open').forEach(e => e.togglePopover(false));

	// Wait for potential exit transitions before removing from the dom
	allTransitionsEnded(dialog).then(() => dialog.remove());

	return result;
}

function allTransitionsEnded(node) {
	return Promise.allSettled(node.getAnimations().map(animation => animation.finished));
}

export async function alert(content, options) {
	await showModal(Alert, { content, ...options });
}
export async function confirm(content, options) {
	const result = await showModal(Confirm, { content, ...options });
	return result === 'OK';
}
export async function choice(content, options) {
	const result = await showModal(Choice, { content, ...options });
	return result;
}
export function toast(content, options) {
	let instance;
	const closed = showModal(Toast, { content, ...options, ref: c => (instance = c) });
	return {
		closed,
		close: () => instance?.close(),
	};
}

export class Modal extends Component {
	c(name) {
		return joinClasses(s[name], this.s?.[name]);
	}
	renderClose() {
		return <Button class={this.c('close')} type="submit" value="CANCEL" formnovalidate icon="cancel" iconSize="large" />;
	}
	renderTitle() {
		return <h2 class={this.c('title')}>{this.props.title || this.title}</h2>;
	}
	renderContent() {
		return <p class={this.c('content')}>{this.props.content || this.content}</p>;
	}
	renderActionButton(primary = false) {
		return (
			<Button
				type="submit"
				value="OK"
				outline={!primary}
				primary={primary}
				danger={this.props.danger}
				icon={this.props.buttonIcon}
				iconAlt={this.props.buttonIconAlt}
			>
				{this.props.button || 'Ok'}
			</Button>
		);
	}
	renderActions() {
		return (
			<div class={this.c('actions')}>
				{this.renderActionButton()}
			</div>
		);
	}
	render() {
		return (
			<dialog class={this.c('wrapper')}>
				<form method="dialog" onSubmit={this.onSubmit}>
					{this.renderTitle()}
					{this.renderContent()}
					{this.renderActions()}
					{this.renderClose()}
				</form>
			</dialog>
		);
	}
}
export class Alert extends Modal {}
export class Confirm extends Modal {
	renderActions() {
		return (
			<div class={s.actions}>
				<Button type="submit" value="CANCEL">{this.props.cancel || 'Cancel'}</Button>
				{this.renderActionButton(true)}
			</div>
		);
	}
}
export class LeaveConfirmationModal extends Modal {
	title = 'Save before exiting?';

	renderActions() {
		return (
			<div class={s.actions} style={{ justifyContent: 'space-between' }}>
				<Button danger outline type="submit" value="CONTINUE">Discard and exit</Button>
				<div class={s.actions} style={{ marginTop: 0 }}>
					<Button type="submit" value="CANCEL">Cancel</Button>
					{canUser('write') && <Button primary type="submit" value="SAVE">Save and exit</Button>}
				</div>
			</div>
		);
	}
}
export class LeaveWithErrorsModal extends Modal {
	title = 'Some errors need to be corrected';
	content = 'You need to fix the highlighted errors before you can save this item. Review the fields marked in red, or discard your changes to exit without saving.';

	renderActions() {
		return (
			<div class={s.actions}>
				<Button type="submit" value="CONTINUE">Discard and exit</Button>
				<Button primary type="submit" value="CANCEL">Review errors</Button>
			</div>
		);
	}
}

export class Choice extends Modal {
	constructor(props) {
		super(props);
		this.state = { choice: props.choices?.[0]?.value };
		this.onChoiceChange = this.onChoiceChange.bind(this);
	}
	onChoiceChange(e) {
		this.setState({ choice: e.target.value });
	}
	renderContent() {
		return (
			<div>
				<p class={s.content}>{this.props.content}</p>
				<div class={s.choices}>
					{this.props.choices.map(choice => (
						<label class={s.choice} key={choice.value}>
							<input type="radio" name="choice" value={choice.value} checked={this.state.choice === choice.value} onChange={this.onChoiceChange} />
							<span>{choice.text}</span>
						</label>
					))}
				</div>
			</div>
		);
	}
	renderActions() {
		return (
			<div class={s.actions}>
				<Button outline type="submit" value="CANCEL">{this.props.cancel || 'Cancel'}</Button>
				{/* value = whatever the user has selected */}
				<Button primary type="submit" value={this.state.choice} disabled={!this.state.choice}>{this.props.button || 'Ok'}</Button>
			</div>
		);
	}
}

const MAX_TOASTS = 8;
export class Toast extends Modal {
	constructor(props) {
		super(props);
		this.onToggle = this.onToggle.bind(this);
		this.onAnyToastClosed = this.onAnyToastClosed.bind(this);
		this.onResize = this.onResize.bind(this);
		this.onAnyToastResized = this.onAnyToastResized.bind(this);
		this.lightDismissMaybe = this.lightDismissMaybe.bind(this);
		this.onTimeout = this.onTimeout.bind(this);
		this.close = this.close.bind(this);
	}
	componentDidMount() {
		super.componentDidMount?.();
		document.addEventListener('pointerdown', this.lightDismissMaybe);
		window.addEventListener('toast-closed', this.onAnyToastClosed);
		window.addEventListener('toast-resized', this.onAnyToastResized);
		this.resizeObserver = new ResizeObserver(this.onResize);
		this.resizeObserver.observe(this.base);
	}
	componentWillUnmount() {
		super.componentWillUnmount?.();
		clearTimeout(this.timeout);
		document.removeEventListener('pointerdown', this.lightDismissMaybe);
		window.removeEventListener('toast-closed', this.onAnyToastClosed);
		window.removeEventListener('toast-resized', this.onAnyToastResized);
		this.resizeObserver.disconnect();
	}
	onToggle(e) {
		if (e.newState === 'closed') {
			// Inform the other toasts so they can shift down if needed
			window.dispatchEvent(new CustomEvent('toast-closed', { detail: { target: this.base } }));
		} else if (e.newState === 'open') {
			// Should we ensure this only runs once? In case the popup reopens before being removed somehow

			let allOpenToasts = [...document.querySelectorAll(`.${s.toast}:popover-open`)];
			if (allOpenToasts.length > MAX_TOASTS) {
				// Close oldest toasts to stay within the limit, if possible
				const toClose = allOpenToasts.length - MAX_TOASTS;
				const closable = allOpenToasts.filter(t => !t.hasAttribute('data-keep'));
				closable.slice(0, toClose).forEach(t => t.hidePopover());
			}
			this.offset = allOpenToasts.reduce((offset, toast) => {
				if (toast === this.base) return offset;
				offset += toast.offsetHeight + 8;
				return offset;
			}, 0);
			this.base.style.setProperty('--toast-offset', `${this.offset}px`);

			if (+this.props.duration) {
				this.timeout = setTimeout(this.onTimeout, +this.props.duration);
			}
		}
	}
	onAnyToastClosed(e) {
		const target = e.detail?.target;
		if (!target || target === this.base) return;
		let allToasts = [...document.querySelectorAll(`.${s.toast}`)];
		// Assumes DOM order is the same as the visual order
		const targetWasLower = allToasts.indexOf(target) < allToasts.indexOf(this.base);
		if (targetWasLower) {
			this.offset -= target.offsetHeight + 8;
			this.base.style.setProperty('--toast-offset', `${this.offset}px`);
		}
	}
	onResize(e) {
		window.dispatchEvent(new CustomEvent('toast-resized', { detail: { target: this.base } }));
	}
	onAnyToastResized(e) {
		const target = e.detail?.target;
		if (!target || target === this.base) return;
		let allToasts = [...document.querySelectorAll(`.${s.toast}`)];
		// Assumes DOM order is the same as the visual order
		const thisIndex = allToasts.indexOf(this.base);
		const targetWasLower = allToasts.indexOf(target) < thisIndex;
		if (targetWasLower) {
			this.offset = allToasts.slice(0, thisIndex).reduce((offset, toast) => offset + toast.offsetHeight + 8, 0);
			this.base.style.setProperty('--toast-offset', `${this.offset}px`);
		}
	}
	lightDismissMaybe(e) {
		// popover="auto" only allows one simultaneous element, but "manual" doesn't use light dismiss, so we manually handle it
		// based on: https://github.com/lukewarlow/dialog-closedby/blob/master/index.js
		if (this.props.stubborn || !e.isTrusted) return;
		let rect = this.base.getBoundingClientRect();
		const inside = (
			rect.top <= e.clientY && e.clientY <= rect.top + rect.height
			&& rect.left <= e.clientX && e.clientX <= rect.left + rect.width
		);
		if (!inside) {
			this.close();
		}
	}
	onTimeout() {
		this.close();
	}
	close() {
		if (this.base?.matches(':popover-open')) {
			this.base.hidePopover();
		}
	}
	renderIcon() {
		const { icon } = this.props;
		if (icon === 'spinner') return <Spinner />;
		return getIcon(icon, { key: 'icon' });
	}
	renderContent() {
		return <p class={joinClasses(s.content, this.s?.content)} key="content">{this.props.content}</p>;
	}
	renderClose() {
		return <Button class={s.close} icon="cancel" iconSize="small" onClick={this.close} key="close" />;
	}
	renderInner() {
		return [
			this.renderIcon(),
			this.renderContent(),
			this.renderClose(),
		];
	}
	render() {
		return (
			<div
				id={this.id}
				class={joinClasses(s.wrapper, s.toast, this.s?.wrapper)}
				popover="manual"
				style={`--toast-offset: ${this.offset || 0}px; --timeout: ${+this.props.duration || 0};`}
				data-keep={this.keep}
				onToggle={this.onToggle}
			>
				{this.renderInner()}
			</div>
		);
	}
}